import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import patientsService, { PatientFormData } from '../../services/patientService';

const CreatePatient: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<PatientFormData>({
    firstName: '',
    lastName: '',
    birthDate: '',
    maritalStatus: '',
    religion: '',
    nationality: '',
    email: '',
    phone: '',
    street: '',
    number: '',
    complement: '',
    district: '',
    city: '',
    state: '',
    zipCode: '',
    paymentMethod: '',
    paymentPeriod: '',
    consultationValue: '',
    issuesInvoice: false
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.firstName || !formData.lastName) {
      Swal.fire({
        icon: 'warning',
        title: 'Atenção!',
        text: 'Nome e sobrenome são obrigatórios.',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    try {
      setLoading(true);
      const patientData = patientsService.extractPatientData(formData);
      await patientsService.create(patientData);

      await Swal.fire({
        icon: 'success',
        title: 'Sucesso!',
        text: 'Paciente cadastrado com sucesso.',
        timer: 2000,
        showConfirmButton: false
      });

      navigate('/patients');
    } catch (err: any) {
      console.error('Erro ao cadastrar paciente:', err);
      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        text: err.message || 'Erro ao cadastrar paciente',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0">Novo Paciente</h1>
            </div>
          </div>
        </div>
      </div>

      <section className="content">
        <div className="container-fluid">
          <div className="card">
            <form onSubmit={handleSubmit}>
              <div className="card-body">
                <div className="card mb-3">
                  <div className="card-header">
                    <h5 className="card-title mb-0">Dados Pessoais</h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="firstName">Nome *</label>
                          <input
                            type="text"
                            className="form-control"
                            id="firstName"
                            name="firstName"
                            placeholder="Digite o nome"
                            value={formData.firstName}
                            onChange={handleChange}
                            disabled={loading}
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="lastName">Sobrenome *</label>
                          <input
                            type="text"
                            className="form-control"
                            id="lastName"
                            name="lastName"
                            placeholder="Digite o sobrenome"
                            value={formData.lastName}
                            onChange={handleChange}
                            disabled={loading}
                            required
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-4">
                        <div className="form-group">
                          <label htmlFor="birthDate">Data de Nascimento</label>
                          <input
                            type="date"
                            className="form-control"
                            id="birthDate"
                            name="birthDate"
                            value={formData.birthDate}
                            onChange={handleChange}
                            disabled={loading}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label htmlFor="maritalStatus">Estado Civil</label>
                          <select
                            className="form-control"
                            id="maritalStatus"
                            name="maritalStatus"
                            value={formData.maritalStatus}
                            onChange={handleChange}
                            disabled={loading}
                          >
                            <option value="">Selecione...</option>
                            <option value="SINGLE">Solteiro(a)</option>
                            <option value="MARRIED">Casado(a)</option>
                            <option value="DIVORCED">Divorciado(a)</option>
                            <option value="WIDOWED">Viúvo(a)</option>
                            <option value="OTHER">Outro</option>
                          </select>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label htmlFor="nationality">Nacionalidade</label>
                          <input
                            type="text"
                            className="form-control"
                            id="nationality"
                            name="nationality"
                            placeholder="Digite a nacionalidade"
                            value={formData.nationality}
                            onChange={handleChange}
                            disabled={loading}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-12">
                        <div className="form-group">
                          <label htmlFor="religion">Religião</label>
                          <input
                            type="text"
                            className="form-control"
                            id="religion"
                            name="religion"
                            placeholder="Digite a religião"
                            value={formData.religion}
                            onChange={handleChange}
                            disabled={loading}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card mb-3">
                  <div className="card-header">
                    <h5 className="card-title mb-0">Informações de Pagamento</h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-3">
                        <div className="form-group">
                          <label htmlFor="paymentMethod">Forma de Pagamento</label>
                          <select
                            className="form-control"
                            id="paymentMethod"
                            name="paymentMethod"
                            value={formData.paymentMethod}
                            onChange={handleChange}
                            disabled={loading}
                          >
                            <option value="">Selecione...</option>
                            <option value="CASH">Dinheiro</option>
                            <option value="CREDIT_CARD">Cartão de Crédito</option>
                            <option value="DEBIT_CARD">Cartão de Débito</option>
                            <option value="PIX">PIX</option>
                            <option value="BANK_TRANSFER">Transferência Bancária</option>
                            <option value="OTHER">Outro</option>
                          </select>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <label htmlFor="paymentPeriod">Período de Pagamento</label>
                          <select
                            className="form-control"
                            id="paymentPeriod"
                            name="paymentPeriod"
                            value={formData.paymentPeriod}
                            onChange={handleChange}
                            disabled={loading}
                          >
                            <option value="">Selecione...</option>
                            <option value="WEEKLY">Semanal</option>
                            <option value="BIWEEKLY">Quinzenal</option>
                            <option value="MONTHLY">Mensal</option>
                            <option value="PER_SESSION">Por Sessão</option>
                          </select>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <label htmlFor="consultationValue">Valor da Consulta</label>
                          <input
                            type="text"
                            className="form-control"
                            id="consultationValue"
                            name="consultationValue"
                            placeholder="R$ 0,00"
                            value={formData.consultationValue}
                            onChange={handleChange}
                            disabled={loading}
                          />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <label>&nbsp;</label>
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="issuesInvoice"
                              name="issuesInvoice"
                              checked={formData.issuesInvoice}
                              onChange={handleChange}
                              disabled={loading}
                            />
                            <label className="form-check-label" htmlFor="issuesInvoice">
                              Emite nota fiscal
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card mb-3">
                  <div className="card-header">
                    <h5 className="card-title mb-0">Contato</h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="email">Email</label>
                          <input
                            type="email"
                            className="form-control"
                            id="email"
                            name="email"
                            placeholder="exemplo@email.com"
                            value={formData.email}
                            onChange={handleChange}
                            disabled={loading}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="phone">Telefone</label>
                          <input
                            type="text"
                            className="form-control"
                            id="phone"
                            name="phone"
                            placeholder="(00) 00000-0000"
                            value={formData.phone}
                            onChange={handleChange}
                            disabled={loading}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card mb-3">
                  <div className="card-header">
                    <h5 className="card-title mb-0">Endereço</h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-8">
                        <div className="form-group">
                          <label htmlFor="street">Rua</label>
                          <input
                            type="text"
                            className="form-control"
                            id="street"
                            name="street"
                            placeholder="Digite o nome da rua"
                            value={formData.street}
                            onChange={handleChange}
                            disabled={loading}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label htmlFor="number">Número</label>
                          <input
                            type="text"
                            className="form-control"
                            id="number"
                            name="number"
                            placeholder="Nº"
                            value={formData.number}
                            onChange={handleChange}
                            disabled={loading}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="complement">Complemento</label>
                          <input
                            type="text"
                            className="form-control"
                            id="complement"
                            name="complement"
                            placeholder="Apto, bloco, etc"
                            value={formData.complement}
                            onChange={handleChange}
                            disabled={loading}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="district">Bairro</label>
                          <input
                            type="text"
                            className="form-control"
                            id="district"
                            name="district"
                            placeholder="Digite o bairro"
                            value={formData.district}
                            onChange={handleChange}
                            disabled={loading}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-5">
                        <div className="form-group">
                          <label htmlFor="city">Cidade</label>
                          <input
                            type="text"
                            className="form-control"
                            id="city"
                            name="city"
                            placeholder="Digite a cidade"
                            value={formData.city}
                            onChange={handleChange}
                            disabled={loading}
                          />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <label htmlFor="state">Estado</label>
                          <input
                            type="text"
                            className="form-control"
                            id="state"
                            name="state"
                            placeholder="UF"
                            maxLength={2}
                            value={formData.state}
                            onChange={handleChange}
                            disabled={loading}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label htmlFor="zipCode">CEP</label>
                          <input
                            type="text"
                            className="form-control"
                            id="zipCode"
                            name="zipCode"
                            placeholder="00000-000"
                            value={formData.zipCode}
                            onChange={handleChange}
                            disabled={loading}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-footer">
                <button type="submit" className="btn btn-success" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                      Cadastrando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save mr-2"></i>
                      Cadastrar Paciente
                    </>
                  )}
                </button>
                <button type="button" className="btn btn-secondary ml-2" onClick={() => navigate('/patients')} disabled={loading}>
                  <i className="fas fa-times mr-2"></i>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </>
  );
};

export default CreatePatient;
